<template>
    <LayoutBase
        :is-top-position="hasMoreAccounts"
        :is-scroll="hasMoreAccounts"
        @scroll="onScroll"
    >
        <template v-if="errorCode || isBlocked" #header>
            <div v-if="isBlocked" class="row row_top-m">
                <UiAlert type="danger">
                    {{ t("blocked.description") }}
                    <UiLink @click="toPasswordChange">{{ t("blocked.btn") }}</UiLink>
                </UiAlert>
            </div>

            <div v-if="errorCode" class="row row_top-m">
                <UiAlert
                    :text="errorCode"
                    type="danger"
                />
            </div>
        </template>

        <template #title>{{ title }}</template>

        <template #description>
            {{ description }}
        </template>

        <template v-if="!isServiceAccount">
            <div>
                <div v-if="hasMoreAccounts" class="row row_bottom-l" role="search">
                    <UiInput v-model:value="search">
                        <template #leading-icon>
                            <IconSearch />
                        </template>
                    </UiInput>
                </div>

                <div :class="$style['system-list']" role="list" aria-label="Account list">
                    <div
                        v-for="account in accountsStore.accounts"
                        :key="account.uid"
                        :class="$style['system-list-btn']"
                        role="listitem"
                    >
                        <span
                            :class="{
                                [$style['system-list-btn__link']]: true,
                                [$style['system-list-btn__link_disabled']]: !account.active,
                            }"
                            @click="getCode(account.uid, query['target-path']?.toString())"
                        >
                            <HighlightText :search="search" :value="account.domain" />
                        </span>

                        <UiTooltip
                            v-if="!account.active"
                            placement="right"
                            :class="$style['system-list-btn__tooltip-btn']"
                        >
                            <template #trigger>
                                <div :class="$style['system-list-btn__tooltip-btn']" />
                            </template>
                            <p>{{ t("accountDisable.top") }}</p>
                            <p>{{ t("accountDisable.bottom") }}</p>
                        </UiTooltip>
                    </div>
                </div>

                <div v-if="isEmpty" :class="$style['empty']">
                    {{ t("empty") }}
                </div>

                <div v-if="isLoading" class="row row_top-m">
                    <IconLoading :class="$style['loader-icon']" />
                </div>
            </div>
        </template>
    </LayoutBase>
</template>

<script lang="ts" setup>
import HighlightText from '@/components/common/HighlightText.vue';
import IconLoading from '@omnica/icon/assets/sprites/actions/loading.svg?component'
import IconSearch from '@omnica/icon/assets/sprites/actions/search.svg?component'
import LayoutBase from '@/components/layouts/LayoutBase.vue';

import debounce from 'lodash/debounce';

import { computed } from 'vue';
import { onBeforeMount } from 'vue';
import privateClient from '@/api/private/client';
import { ref } from 'vue';
import { useAccountsStore } from '@/stores/accounts';
import { useAnalytics } from '@/plugins/analytics';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { watch } from 'vue';

const { t } = useI18n();
const { query } = useRoute();
const router = useRouter();
const accountsStore = useAccountsStore();
const analytics = useAnalytics();

const { user, isServiceAccount, isBlocked } = useAuthStore();

const search = ref('');
const isLoading = ref(false);
const errorCode = ref('');

const title = computed(() => {
    if (isServiceAccount) {
        return t('systemAccount')
    }

    return t('title')
});

const description = computed((): string => {
    if (isServiceAccount) {
        return t('systemAccountDescription')
    }

    return t('description', { email: user?.email, count: accountsStore.totalCount })
});

const hasMoreAccounts = computed(() => accountsStore.totalCount > 5);

const isEmpty = computed((): boolean => {
    return !isLoading.value &&
        search.value !== '' &&
        accountsStore.totalCount > 0 &&
        accountsStore.accounts.length === 0
});

const toPasswordChange = () => router.push({ name: 'password-change' })

watch(search, async () => {
    await accountsStore.clear()
    loadAccountsDebounce()
})

onBeforeMount(async () => {
    await accountsStore.clear()
    await autoCode()
    await loadAccounts()
});

const onScroll = ({ scrollTop, clientHeight, scrollHeight }: {scrollTop: number, clientHeight: number, scrollHeight: number}) => {
    if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadAccountsDebounce()
    }
};

const loadAccounts = async () => {
    if (isServiceAccount) {
        return
    }

    isLoading.value = true
    await accountsStore.fetchAccounts(search.value);
    isLoading.value = false
}

const loadAccountsDebounce = debounce(loadAccounts, 300)

const autoCode = async () => {
    const uid = query['target-crm']?.toString()

    if (!uid?.length) {
        return;
    }

    await getCode(uid, query['target-path']?.toString())
}

const getCode = (uid: string, path: string | undefined = undefined) => {
    errorCode.value = ''

    privateClient
        .code(uid)
        .then(({ data, error }) => {
            if (error != null) {
                if (error.code === 404) {
                    errorCode.value = t('noTarget');
                } else if (error.code === 403) {
                    errorCode.value = t('targetInactive');
                }
            }

            return data
        })
        .then((data) => {
            analytics.trackEvent('leadFormLogin')
            analytics.trackEvent('authLoginEnter')

            return data
        })
        .then((data) => {
            if (data === null) {
                return
            }

            let targetUrl = data.data.domain

            if (path) {
                targetUrl += decodeURIComponent(path)
            }

            const url = new URL(targetUrl)

            url.searchParams.set('sso_code', data.data.code)
            if (data.data.remember_me) {
                url.searchParams.set('sso_remember_me', '1')
            }

            location.href = url.toString();
        })
};
</script>

<style lang="less" module>
@import (reference) "@omnica/base/assets/stylesheets/geometry.less";
@import (reference) "@omnica/base/assets/stylesheets/layout.less";
@import (reference) "@omnica/base/assets/stylesheets/palette.less";
@import (reference) "@omnica/base/assets/stylesheets/typography.less";

.system-list-btn {
  position: relative;

  & + & {
    margin-top: @spacing-s;
  }

  &__link {
    cursor: pointer;
    display: block;
    color: @black-500;
    padding: @spacing-s;
    border: 1px solid @grey-500;
    border-radius: @border-radius-lg;
    .text-regular-accent;
    text-decoration: none;
    transition: 0.2s linear;

    &_disabled {
      background: @grey-200;
      border-color: @grey-500;
      color: @grey-900;
      cursor: default;
    }

    &:not(.system-list-btn_disabled):hover {
      background: @blue-100;
      border-color: @blue-500;
    }
  }

  &__tooltip-btn {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    cursor: default;

    div {
        width: 100%;
        height: 100%;
    }
  }

  &__tooltip-wrapper {
    width: 100%;
    height: 100%;
  }
}

.loader-icon {
  display: block;
  .square(24px);
  color: @blue-500;
  margin: 0 auto;
}

.empty {
    text-align: center;
}
</style>

<i18n locale="ru_RU" lang="json">
{
  "blocked": {
      "description": "В связи с подозрительной активностью на вашем аккаунте доступ к системам был ограничен, для восстановления доступа смените пароль на новый.",
      "btn": "Сменить пароль"
  },
  "title": "Выберите систему",
  "description": "Почта {email} не зарегистрирована ни в одной системе | Почта {email} зарегистрирована в {count} системе | Почта {email} зарегистрирована в {count} системах. Выберите, в какую хотите зайти",
  "accountDisable": {
      "top": "Ваш профиль не\u00a0активен в\u00a0этой системе.",
      "bottom": "Для входа свяжитесь с\u00a0администратором системы."
  },
  "systemAccount": "Техническая поддержка",
  "systemAccountDescription": "Вы вошли под аккаунтом технической поддержки. Чтобы перейти в целевую систему, откройте прямую ссылку.",
  "noTarget": "Вы не имеете доступа к запрошенной системе.",
  "targetInactive": "Ваш профиль в запрошенной системе неактивен.",
  "empty": "Нет систем удовлетворяющих вашему запросу."
}
</i18n>

<i18n locale="en_GB" lang="json">
{
    "blocked": {
        "description": "Due to suspicious activity on your account, access to the systems has been restricted. To regain access, please change your password to a new one.",
        "btn": "Change Password"
    },
    "title": "Choose a system",
    "description": "Mail {email} is not registered in any system | Mail {email} is registered in the {count} system | Mail {email} is registered in {count} systems, select which one you want to log in",
    "accountDisable": {
        "top": "Your profile is not active on this system.",
        "bottom": "To log in, contact the system administrator."
    },
    "systemAccount": "Support account",
    "systemAccountDescription": "You are logged in with a technical support account. To go to the target system, open the direct link.",
    "noTarget": "You do not have access to the requested system.",
    "targetInactive": "Your profile in the requested system is inactive.",
    "empty": "There are no systems that meet your request."
}
</i18n>

<i18n locale="es_ES" lang="json">
{
    "blocked": {
        "description": "Debido a actividad sospechosa en su cuenta, el acceso a los sistemas ha sido restringido. Para recuperar el acceso, cambie su contraseña por una nueva.",
        "btn": "Cambiar Contraseña"
    },
    "title": "Selecciona el sistema",
    "description": "El email {email} no está registrado en ningún sistema | El email {email} está registrado en {count} sistema | El email {email} está registrado en {count} sistemas, seleccione a cual desea ingresar",
    "accountDisable": {
        "top": "Tu perfil no está activo en este sistema.",
        "bottom": "Para entrar ponte en contacto con el administrador del sistema."
    },
    "systemAccount": "Cuenta de soporte",
    "systemAccountDescription": "Ha iniciado sesión con una cuenta de soporte técnico. Para ir al sistema de destino, abra el enlace directo.",
    "noTarget": "No tiene acceso al sistema solicitado.",
    "targetInactive": "Su perfil en el sistema solicitado está inactivo.",
    "empty": "No hay sistemas que cumplen tu solicitud."
}
</i18n>
